/**
 * WalkCompanion Challenge Service
 * High-level challenge operations and business logic
 */

import { FirebaseService } from './FirebaseService';
import {
  Challenge,
  ChallengeWithEligibility,
  User,
  ChallengeLevel,
  ChallengeType,
  DailyCompletion,
  ApiResponse
} from '../types';
import { validateChallengeInput } from '../utils/validationUtils.js';
import { ref, remove } from 'firebase/database';
import { sanitizeFirebaseKey } from '../utils/validationUtils';
import { isChallengeActive, isUserEligibleForChallenge } from './challenge/challengeValidator';

export class ChallengeService {
  private firebaseService: FirebaseService;
  private isDebugMode: boolean;

  constructor(firebaseService: FirebaseService) {
    this.firebaseService = firebaseService;
    // Check if we're in debug mode (web app only)
    this.isDebugMode = typeof window !== 'undefined' && (process.env.REACT_APP_DEBUG === 'true' || process.env.REACT_APP_DEBUG === '1');
  }

  // ============================================================================
  // CHALLENGE DISCOVERY
  // ============================================================================

  /**
   * Get all challenges for a user with eligibility filtering
   */
  async getUserChallenges(userId: string, user?: User): Promise<ChallengeWithEligibility[]> {
    try {
      const challenges = await this.firebaseService.loadUserChallenges(userId, user);

      // Sort challenges by priority: personal > team > organization > global
      const priorityOrder = { personal: 0, team: 1, organization: 2, global: 3 };

      return challenges.sort((a, b) => {
        const priorityA = priorityOrder[a.level] ?? 4;
        const priorityB = priorityOrder[b.level] ?? 4;
        return priorityA - priorityB;
      });
    } catch (error) {
      console.error('❌ Error getting user challenges:', error);
      throw error;
    }
  }

  /**
   * Get challenges by level
   */
  async getChallengesByLevel(userId: string, level: ChallengeLevel, user?: User): Promise<ChallengeWithEligibility[]> {
    const allChallenges = await this.getUserChallenges(userId, user);
    return allChallenges.filter(challenge => challenge.level === level);
  }

  /**
   * Get challenges by type
   */
  async getChallengesByType(userId: string, type: ChallengeType, user?: User): Promise<ChallengeWithEligibility[]> {
    const allChallenges = await this.getUserChallenges(userId, user);
    return allChallenges.filter(challenge => challenge.type === type);
  }

  /**
   * Get active challenges (user has joined)
   */
  async getActiveChallenges(userId: string, user?: User): Promise<ChallengeWithEligibility[]> {
    if (this.isDebugMode) {
      console.log('[ChallengeService] getActiveChallenges called with userId:', userId);
      console.log('[ChallengeService] User object:', user);
    } else {
      console.log('[ChallengeService] getActiveChallenges called with userId: [hidden - set REACT_APP_DEBUG=true to show]');
      console.log('[ChallengeService] User object: [hidden - set REACT_APP_DEBUG=true to show]');
    }

    const [challenges, participations] = await Promise.all([
      this.getUserChallenges(userId, user),
      this.firebaseService.loadUserParticipations(userId, user)
    ]);

    if (this.isDebugMode) {
      console.log('[ChallengeService] Found challenges:', challenges.length);
      console.log('[ChallengeService] Found participations:', participations.length);
      console.log('[ChallengeService] Participations:', participations);
    }

    // Filter for active challenges: check both 'status === in_progress' and 'isActive === true'
    // This supports both new format (with status field) and old format (with isActive field)
    const activeChallengeIds = new Set(
      participations
        .filter(p => {
          // New format: has status field
          if (p.status === 'in_progress') return true;
          // Old format: has isActive field but no status (backward compatibility)
          if (p.isActive === true && !p.status) return true;
          return false;
        })
        .map(p => p.challengeId)
    );

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/dd95de8b-4aae-4806-ab49-a2b65a05ac50', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        location: 'ChallengeService.ts:96',
        message: 'getActiveChallenges filtering',
        data: {
          totalParticipations: participations.length,
          activeChallengeIds: Array.from(activeChallengeIds),
          participationDetails: participations.map((p: any) => ({
            challengeId: p.challengeId,
            status: p.status,
            isActive: p.isActive,
            isIncluded: p.status === 'in_progress' || (p.isActive === true && !p.status)
          }))
        },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'run1',
        hypothesisId: 'G'
      })
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    }).catch(() => {});
    // #endregion

    if (this.isDebugMode) {
      console.log('[ChallengeService] Active challenge IDs:', Array.from(activeChallengeIds));
      console.log('[ChallengeService] Participation details:', participations.map(p => ({
        challengeId: p.challengeId,
        status: p.status,
        isActive: p.isActive,
        isIncluded: p.status === 'in_progress' || (p.isActive === true && !p.status)
      })));
    }

    const activeChallenges = challenges.filter(challenge => activeChallengeIds.has(challenge.id));
    if (this.isDebugMode) {
      console.log('[ChallengeService] Returning active challenges:', activeChallenges.length);
    }

    return activeChallenges;
  }

  /**
   * Get completed challenges
   */
  async getCompletedChallenges(userId: string, user?: User): Promise<ChallengeWithEligibility[]> {
    const [challenges, participations] = await Promise.all([
      this.getUserChallenges(userId, user),
      this.firebaseService.loadUserParticipations(userId)
    ]);

    const completedChallengeIds = new Set(
      participations
        .filter(p => p.status === 'completed')
        .map(p => p.challengeId)
    );

    return challenges.filter(challenge => completedChallengeIds.has(challenge.id));
  }

  // ============================================================================
  // CHALLENGE PARTICIPATION
  // ============================================================================

  /**
   * Join a challenge with validation
   */
  async joinChallenge(userId: string, challengeId: string, user?: User): Promise<ApiResponse<void>> {
    try {
      // Validate user can join
      const challenge = await this.firebaseService.getChallenge(challengeId);
      if (!challenge) {
        return { success: false, error: 'Challenge not found' };
      }

      // Check if already participating
      const existingParticipation = await this.firebaseService.getParticipation(userId, challengeId);
      if (existingParticipation) {
        return { success: false, error: 'Already participating in this challenge' };
      }

      // Check eligibility
      const eligibility = this.checkEligibility(challenge, user);
      if (!eligibility.isEligible) {
        return { success: false, error: eligibility.reason || 'Not eligible for this challenge' };
      }

      await this.firebaseService.joinChallenge(userId, challengeId, user);
      return { success: true };

    } catch (error) {
      console.error('❌ Error joining challenge:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to join challenge'
      };
    }
  }

  /**
   * Leave a challenge
   */
  async leaveChallenge(userId: string, challengeId: string): Promise<ApiResponse<void>> {
    try {
      const participation = await this.firebaseService.getParticipation(userId, challengeId);
      if (!participation) {
        return { success: false, error: 'Not participating in this challenge' };
      }

      if (participation.status === 'completed') {
        return { success: false, error: 'Cannot leave a completed challenge' };
      }

      // Remove participation - use original userId without sanitization to match database rules
      const participationRef = ref(this.firebaseService.getDatabase(), `participations/${userId}/${challengeId}`);
      await remove(participationRef);

      return { success: true };

    } catch (error) {
      console.error('❌ Error leaving challenge:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to leave challenge'
      };
    }
  }

  // ============================================================================
  // PROGRESS UPDATES
  // ============================================================================

  /**
   * Update challenge progress with validation
   */
  async updateProgress(
    userId: string,
    challengeId: string,
    value: number,
    isDailyCompletion: boolean = false
  ): Promise<ApiResponse<void>> {
    try {
      // Validate input
      const validation = validateChallengeInput(value);
      if (!validation.isValid) {
        return { success: false, error: validation.error || 'Invalid input' };
      }

      // Get challenge and participation
      const [challenge, participation] = await Promise.all([
        this.firebaseService.getChallenge(challengeId),
        this.firebaseService.getParticipation(userId, challengeId)
      ]);

      if (!challenge) {
        return { success: false, error: 'Challenge not found' };
      }

      if (!participation) {
        return { success: false, error: 'Not participating in this challenge' };
      }

      if (participation.status === 'completed') {
        return { success: false, error: 'Challenge already completed' };
      }

      // Validate against challenge constraints
      if (challenge.inputValidation) {
        const { minValue, maxValue } = challenge.inputValidation;
        if (minValue !== undefined && value < minValue) {
          return { success: false, error: `Value must be at least ${minValue}` };
        }
        if (maxValue !== undefined && value > maxValue) {
          return { success: false, error: `Value must be at most ${maxValue}` };
        }
      }

      await this.firebaseService.updateChallengeProgress(userId, challengeId, value, isDailyCompletion);
      return { success: true };

    } catch (error) {
      console.error('❌ Error updating progress:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update progress'
      };
    }
  }

  /**
   * Submit daily completion for self-reported challenges
   */
  async submitDailyCompletion(
    userId: string,
    challengeId: string,
    value: number
  ): Promise<ApiResponse<void>> {
    try {
      const challenge = await this.firebaseService.getChallenge(challengeId);
      if (!challenge) {
        return { success: false, error: 'Challenge not found' };
      }

      if (challenge.entryFrequency !== 'daily') {
        return { success: false, error: 'Challenge does not support daily entries' };
      }

      if (challenge.metric !== 'selfReported') {
        return { success: false, error: 'Challenge is not self-reported' };
      }

      return await this.updateProgress(userId, challengeId, value, true);

    } catch (error) {
      console.error('❌ Error submitting daily completion:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit daily completion'
      };
    }
  }

  // ============================================================================
  // CHALLENGE ANALYTICS
  // ============================================================================

  /**
   * Get challenge statistics for a user
   */
  async getChallengeStats(userId: string): Promise<{
    totalChallenges: number;
    activeChallenges: number;
    completedChallenges: number;
    totalPoints: number;
    averageProgress: number;
  }> {
    try {
      const participations = await this.firebaseService.loadUserParticipations(userId);

      const totalChallenges = participations.length;
      const activeChallenges = participations.filter(p => p.status === 'in_progress').length;
      const completedChallenges = participations.filter(p => p.status === 'completed').length;
      const totalPoints = participations.reduce((sum, p) => sum + p.pointsEarned, 0);
      const averageProgress = totalChallenges > 0
        ? participations.reduce((sum, p) => sum + p.progress, 0) / totalChallenges
        : 0;

      return {
        totalChallenges,
        activeChallenges,
        completedChallenges,
        totalPoints,
        averageProgress
      };

    } catch (error) {
      console.error('❌ Error getting challenge stats:', error);
      throw error;
    }
  }

  /**
   * Get daily completion streak for a challenge
   */
  async getDailyStreak(userId: string, challengeId: string): Promise<number> {
    try {
      const participation = await this.firebaseService.getParticipation(userId, challengeId);
      if (!participation || !participation.dailyCompletions) {
        return 0;
      }

      const sortedCompletions = Object.values(participation.dailyCompletions || {})
        .filter(dc => dc.isCompleted)
        .sort((a, b) => b.date.localeCompare(a.date));

      let streak = 0;
      let currentDate = new Date();

      for (const completion of sortedCompletions) {
        const completionDate = new Date(completion.date);
        const daysDiff = Math.floor((currentDate.getTime() - completionDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDiff === streak) {
          streak++;
        } else {
          break;
        }
      }

      return streak;

    } catch (error) {
      console.error('❌ Error getting daily streak:', error);
      return 0;
    }
  }

  /**
   * Get challenge progress summary
   */
  async getProgressSummary(userId: string, challengeId: string): Promise<{
    currentValue: number;
    targetValue: number;
    progress: number;
    remaining: number;
    isCompleted: boolean;
    dailyCompletions: DailyCompletion[];
    streak: number;
  } | null> {
    try {
      const [challenge, participation] = await Promise.all([
        this.firebaseService.getChallenge(challengeId),
        this.firebaseService.getParticipation(userId, challengeId)
      ]);

      if (!challenge || !participation) {
        return null;
      }

      // Helper functions to get target value and unit from new or legacy fields
      const getTargetValue = (challenge: any) => challenge.measurementTarget || challenge.targetValue;
      const getTargetUnit = (challenge: any) => challenge.measurementUnit || challenge.targetUnit;

      // Calculate remaining progress
      const targetValue = getTargetValue(challenge);
      const remaining = Math.max(0, targetValue - participation.currentValue);
      const progress = Math.min(100, (participation.currentValue / targetValue) * 100);
      const streak = await this.getDailyStreak(userId, challengeId);

      return {
        currentValue: participation.currentValue,
        targetValue: targetValue,
        progress,
        remaining,
        isCompleted: participation.status === 'completed',
        dailyCompletions: Object.values(participation.dailyCompletions || {}),
        streak
      };

    } catch (error) {
      console.error('❌ Error getting progress summary:', error);
      return null;
    }
  }

  // ============================================================================
  // CHALLENGE RECOMMENDATIONS
  // ============================================================================

  /**
   * Get available challenges for a user (challenges they can join)
   */
  async getAvailableChallenges(userId: string, user?: User): Promise<ChallengeWithEligibility[]> {
    try {
      if (this.isDebugMode) {
        console.log('[ChallengeService] getAvailableChallenges called with userId:', userId);
      }

      // Get all challenges the user is eligible for
      const allChallenges = await this.getUserChallenges(userId, user);
      const eligibleChallenges = allChallenges.filter(c => c.isEligible);

      if (this.isDebugMode) {
        console.log('[ChallengeService] Found eligible challenges:', eligibleChallenges.length);
      }

      // Get user's current participations to filter out already joined challenges
      const participations = await this.firebaseService.loadUserParticipations(userId, user);
      const joinedChallengeIds = new Set(
        participations
          .filter(p => p.status === 'in_progress' || p.status === 'completed')
          .map(p => p.challengeId)
      );

      if (this.isDebugMode) {
        console.log('[ChallengeService] User has joined challenges:', Array.from(joinedChallengeIds));
      }

      // Filter out challenges the user has already joined
      const availableChallenges = eligibleChallenges.filter(challenge => !joinedChallengeIds.has(challenge.id));

      if (this.isDebugMode) {
        console.log('[ChallengeService] Available challenges to join:', availableChallenges.length);
      }

      return availableChallenges;

    } catch (error) {
      console.error('❌ Error getting available challenges:', error);
      return [];
    }
  }

  /**
   * Get recommended challenges for a user
   */
  async getRecommendedChallenges(userId: string, user?: User, limit: number = 5): Promise<ChallengeWithEligibility[]> {
    try {
      const allChallenges = await this.getUserChallenges(userId, user);
      const eligibleChallenges = allChallenges.filter(c => c.isEligible);

      // Simple recommendation algorithm: prioritize personal challenges and active challenges
      const recommendations = eligibleChallenges
        .sort((a, b) => {
          // Personal challenges first
          if (a.level === 'personal' && b.level !== 'personal') return -1;
          if (b.level === 'personal' && a.level !== 'personal') return 1;

          // Then by points (higher points = more valuable)
          return b.points - a.points;
        })
        .slice(0, limit);

      return recommendations;

    } catch (error) {
      console.error('❌ Error getting recommended challenges:', error);
      return [];
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Check if a challenge is eligible for a user
   */
  private checkEligibility(challenge: Challenge, user?: User): { isEligible: boolean; reason?: string } {
    if (!user) {
      return { isEligible: false, reason: 'User not authenticated' };
    }

    // Check if challenge is active
    if (!challenge.isActive) {
      return { isEligible: false, reason: 'Challenge is not active' };
    }

    // Check date eligibility
    const now = new Date();
    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);

    if (now < startDate) {
      return { isEligible: false, reason: 'Challenge has not started yet' };
    }

    if (now > endDate) {
      return { isEligible: false, reason: 'Challenge has ended' };
    }

    // Check organization eligibility
    if (challenge.level === 'organization' && challenge.organizationId) {
      // Check direct organization membership (existing logic)
      if (user.organizationId === challenge.organizationId) {
        return { isEligible: true };
      }

      // Check family member status - family members can join challenges from their family's organization
      if (user.familyStatus === 'family_member' && user.familyMemberOf) {
        // We need to check if the family member's inviting user belongs to this organization
        // This will be handled by checking the family member's organization context
        // which should be set when they accept the invitation
        if (user.organizationId === challenge.organizationId) {
          return { isEligible: true };
        }
      }

      return { isEligible: false, reason: 'Challenge is for a different organization' };
    }

    // Check team eligibility
    if (challenge.level === 'team' && challenge.teamId) {
      // Check direct team membership (existing logic)
      if (user.teamId === challenge.teamId) {
        return { isEligible: true };
      }

      // NEW: Check if family member has access to team's organization
      if (user.familyStatus === 'family_member' && user.organizationId === challenge.organizationId) {
        return { isEligible: true };
      }

      return { isEligible: false, reason: 'Challenge is for a different team' };
    }

    return { isEligible: true };
  }

  /**
   * Get Firebase service instance
   */
  getFirebaseService(): FirebaseService {
    return this.firebaseService;
  }

  // ============================================================================
  // STATIC HELPER METHODS (for framework-independent operations)
  // ============================================================================

  /**
   * Get challenges with eligibility for a user (static helper)
   */
  static getChallengesWithEligibility(
    challenges: Challenge[],
    user: User
  ): ChallengeWithEligibility[] {
    return challenges.map((challenge) => {
      const eligibility = isUserEligibleForChallenge(challenge, user);
      return {
        ...challenge,
        isEligible: eligibility.eligible,
        eligibilityReason: eligibility.reason,
      };
    });
  }

  /**
   * Filter active challenges (static helper)
   */
  static getActiveChallenges(challenges: Challenge[]): Challenge[] {
    return challenges.filter((challenge) => isChallengeActive(challenge));
  }
} 